/** Use guardedCall in a limited way - when a 3rd-party library or similar black box of code could
    fail unexpectedly/intermittently with unhelpful errors and you need help to track down the cause. For
    any use consider if the end result is hiding errors that would otherwise be helpful to see.

    Example usage:
    export function area() {
      return guardedCall({func:turfArea, args:[...arguments], library:'Turf library', funcName:'area'});
    }

    For passing "arguments" like above, the classic function syntax is needed (no arrow function). */

import { workerLog, workerLogError } from '../scripts/editZones.worker';
import { MAX_ARGS_SIZE } from '../constants/guarded-call';

export const DISABLE_VALIDATION = { guardedCallOption:'disableValidation' };
const _argsHasDisableValidation = (args) => args.some(arg => arg === DISABLE_VALIDATION);

/** Log an error message for a failed API call.
    @param args Array of arguments to pass.
    @param funcName Name of function to use in logging messages.
    @param library Optional name of library to use in logging messages.
    @param error Optional JS Error object. Specify errorMessage if not passing error.
    @param errorMessage Optional description of error. Specify error if not passing errorMessage.
 */
export const logApiError = ({args, funcName, library, error, errorMessage}) => {
  const concatLibrary = library ? `in ${library} library ` : '';
  const context = { args:{...args} };
  const message = `Call to ${funcName}() ${concatLibrary}failed.`;
  if (!error) { error = new Error(errorMessage || 'no cause of error available'); }
  workerLogError({message, error, context});
  return message;
};

/** Call an API with trapping for exceptions that logs params passed to API along with error.
    @param func Function to call.
    @param args Array of arguments to pass.
    @param library Optional name of library to use in logging messages.
    @param funcName Name of function to use in logging messages.
    @param onValidateResult (Optional) If specified, validator function will be called to verify return value of API.
      Validator function will be passed the API return value and message-logging function. It should throw a
      descriptive error if a problem is found--the error will be logged in this case. Validator function can optionally
      call the passed message-logging function to log warnings without causing validation to fail.
    @param preLog If true, then information will be logged to console *before* the call is made. (Useful in
                  some troubleshooting situations. Default is false.
    @param translateUndefined If specified, and API return value is undefined, the result will be changed to this
      value. This allows guardedCall() to reserve undefined for meaning the function has failed. */
export const guardedCall = ({func, args, library, funcName, preLog, onValidateResult, translateUndefined}) => {
  const stringArgs = JSON.stringify(args);
  if (preLog) {
    const concatLibrary = library ? `in ${library} library ` : '';
    workerLog(`Calling ${funcName}() ${concatLibrary} with ${stringArgs}.`);
  }

  const logMessageFunc = (error) => {
    if (typeof error === 'string') { error = new Error(error); }
    // If args is too long, truncate it
    if (stringArgs.length > MAX_ARGS_SIZE ) {
      const truncatedArgs = {
        preTruncatedLength: stringArgs.length,
        reasonForTruc: 'args too large for Sentry',
        truncatedArgs: stringArgs.substring(0, MAX_ARGS_SIZE)
      };
      logApiError({args: truncatedArgs, funcName, library, error});
    }
    else {
      logApiError({args, funcName, library, error});
    }
  };
  try {
    let result = func(...args);
    if (result === undefined && translateUndefined !== undefined) { result = translateUndefined; }
    if (onValidateResult && !_argsHasDisableValidation(args)) { onValidateResult(result, logMessageFunc); }
    return result;
  } catch (error) {
    logMessageFunc(error);
    return undefined;
  }
};
